import { AppNode, NodeType } from "@features/builder/graph/types"
import { linkAtom, linkDialogNodeAtom } from "@components/NodeLink/linkAtom"
import NodeLinkChips from "@components/NodeLink/NodeLinkChips"
import { linkEncode, linkParse } from "@components/NodeLink/linkHelpers"
import { useNodeLinkValidation } from "@components/NodeLink/useNodeLinkValidation"
import PropertyField from "@features/builder/NodeDrawer/NodeForm/PropertyField"
import { Delete, OpenInNew } from "@mui/icons-material"
import { FormHelperText, Grid, IconButton, Link, Tooltip } from "@mui/material"
import { useSetAtom } from "jotai"
import { Control, FieldValues, Path, useWatch } from "react-hook-form"
import { useGetUnpackedGraphDataQuery } from "@store/services/graphsService"
import { getParentsList } from "@utils/node/getParentsList"

type NodeLinkFieldProps<TFieldValues extends FieldValues> = {
  node?: AppNode
  isFormDisabled: boolean
  control: Control<TFieldValues>
  name: Path<TFieldValues>
  onRemove?: (nodeId: string) => void
  limitChipWidth?: boolean
  label?: string
}
const NodeLinkField = <TFieldValues extends FieldValues>(
  props: NodeLinkFieldProps<TFieldValues>,
) => {
  const setLinkDialogNode = useSetAtom(linkDialogNodeAtom)
  const setLink = useSetAtom(linkAtom)
  const link = useWatch({ control: props.control, name: props.name })

  const haveParent = props.node ? getParentsList(props.node).length > 0 : false

  const parsedLink = linkParse(link)
  const { hasError, errorMessages } = useNodeLinkValidation(parsedLink, props.node)
  const showErrors = !!link && hasError

  const skip =
    !parsedLink?.org ||
    !parsedLink?.div ||
    !parsedLink?.prod ||
    !parsedLink?.family ||
    !parsedLink?.version
  const graphKey = `${parsedLink?.org}#${parsedLink?.div}#${parsedLink?.prod}#${parsedLink?.family}_${parsedLink?.version}`
  const {
    data: linkedGraphData,
    error: linkedGraphDataError,
    isSuccess: isSuccessLinkedGraphData,
  } = useGetUnpackedGraphDataQuery(graphKey, { skip })

  const isDeleteButtonDisabled = props.isFormDisabled || haveParent
  const isInputDisabled =
    props.isFormDisabled ||
    haveParent ||
    props.node?.type === NodeType.Table ||
    props.node?.type === NodeType.ScenarioTable ||
    props.node?.type === NodeType.ForecastTable

  const handleClick = () => {
    if (isInputDisabled) return

    setLinkDialogNode(props.node)
    if (parsedLink) setLink(parsedLink)
  }

  const isLinkedGraphAvailable =
    parsedLink && linkedGraphData && !linkedGraphDataError && isSuccessLinkedGraphData

  return (
    <Grid container item wrap="nowrap" gap={1} alignItems="center">
      <Grid item xs>
        <Tooltip
          title={haveParent ? "Cannot set a link because this node has a parent node(s)" : ""}
        >
          <span>
            <PropertyField
              InputProps={{
                readOnly: true,
                startAdornment: (
                  <NodeLinkChips
                    link={link ?? ""}
                    type={props.node?.type}
                    limitChipWidth={props.limitChipWidth}
                  />
                ),
                inputProps: { sx: { width: link ? 0 : "unset" } }, // do not show real input value at all, just chips
              }}
              control={props.control}
              label={props.label ?? "Link"}
              name={props.name}
              error={showErrors}
              sx={{ ".MuiInputBase-root": { p: 1 }, cursor: "pointer" }}
              disabled={isInputDisabled}
              onClick={handleClick}
            />
            {showErrors &&
              errorMessages.map((err, idx) => (
                <FormHelperText key={idx} error>
                  - {err}
                </FormHelperText>
              ))}
          </span>
        </Tooltip>
      </Grid>

      {link && (
        <>
          <Grid item>
            <Tooltip
              title={isLinkedGraphAvailable ? "View linked graph" : "Linked graph is not available"}
            >
              <span>
                <IconButton
                  size="small"
                  component={Link}
                  disabled={!isLinkedGraphAvailable}
                  href={
                    parsedLink
                      ? `/ScenarioDesign?graphKey=${encodeURIComponent(
                          `${linkEncode(
                            parsedLink.org,
                            parsedLink.div,
                            parsedLink.prod,
                            parsedLink.family,
                            parsedLink.version,
                          )}`,
                        )}`
                      : undefined
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <OpenInNew fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Grid>

          <Grid item>
            <Tooltip title="Remove link">
              <span>
                <IconButton
                  color="error"
                  size="small"
                  onClick={() => props.onRemove?.(props.node?.id ?? "")}
                  disabled={isDeleteButtonDisabled}
                >
                  <Delete />
                </IconButton>
              </span>
            </Tooltip>
          </Grid>
        </>
      )}
    </Grid>
  )
}

export default NodeLinkField
